import { useRef, useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { FaArrowLeft, FaCalendarAlt, FaClock, FaUser, FaEnvelope, FaPhone, FaCalendarPlus, FaCheckCircle } from "react-icons/fa";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import api from "../../utils/api";
import "./WebinarRegister.css";

export default function WebinarRegister() {
  const { slug } = useParams();

  // Webinar loading state
  const [webinar, setWebinar] = useState(null);
  const [loadingWebinar, setLoadingWebinar] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    answers: {},
  });

  // Form submission & error states 
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null | 'success' | 'error'
  const [errors, setErrors] = useState({}); // { field: [messages] } or { general: [message] }
  const [registrationData, setRegistrationData] = useState(null);

  // Focus tracking for premium inputs
  const [focusedField, setFocusedField] = useState(null);

  const formRef = useRef(null);

  useEffect(() => {
    const fetchWebinar = async () => {
      setLoadingWebinar(true);
      try {
        const res = await api.get(`/webinars/${slug}`);
        setWebinar(res.data.data);
        
        // Initialize answers for dynamic form fields
        const initialAnswers = {};
        (res.data.data.form_fields || []).forEach((field) => {
          initialAnswers[field.key] = field.type === "checkbox" ? false : "";
        });
        
        setFormData(prev => ({
          ...prev,
          answers: initialAnswers
        }));
      } catch (err) {
        if (err.response?.status === 404) {
          setNotFound(true);
        }
      } finally {
        setLoadingWebinar(false);
      }
    };
    fetchWebinar();
  }, [slug]);

  // Update base form fields (name, email, phone)
  const handleBaseChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear field error when user starts typing
    setErrors(prev => ({
      ...prev,
      [name]: undefined
    }));
  };

  // Update dynamic form field answers
  const handleAnswerChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      answers: {
        ...prev.answers,
        [key]: value
      }
    }));
    // Clear field error when user updates answer
    setErrors(prev => ({
      ...prev,
      [`answers.${key}`]: undefined
    }));
  };

  // Helper to resolve error message for a field
  const getFieldError = (key) => {
    const errorList = errors[key];
    return errorList && errorList.length > 0 ? errorList[0] : null;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  };

  // Google Calendar Link Generator
  const getGoogleCalendarLink = () => {
    if (!webinar) return "#";
    const start = new Date(webinar.starts_at);
    const end = new Date(start.getTime() + (webinar.duration_minutes || 60) * 60 * 1000);
    
    const formatCalendarDate = (date) => {
      return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    };
    
    const title = encodeURIComponent(`Webinar: ${webinar.title}`);
    const dates = `${formatCalendarDate(start)}/${formatCalendarDate(end)}`;
    const details = encodeURIComponent(`${webinar.description || "GritinAI Webinar Session"}\n\nJoin link will be sent to your email: ${registrationData?.email || formData.email}`);
    const location = encodeURIComponent("Online (Link in email)");
    
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dates}&details=${details}&location=${location}&sf=true&output=xml`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formEl = formRef.current;
    if (!formEl || !formEl.checkValidity()) {
      formEl?.reportValidity();
      return;
    }
    
    if (submitting) return;

    setSubmitting(true);
    setErrors({});

    // Build payload from formData
    const payload = {
      name: formData.name,
      email: formData.email,
      answers: formData.answers
    };
    
    if (formData.phone?.trim()) {
      payload.phone = formData.phone.trim();
    }

    try {
      const res = await api.post(`/webinars/${slug}/register`, payload);
      setSubmitStatus("success");
      setRegistrationData(res.data.data);
      toast.success(res.data.message || "Registration successful!");
    } catch (err) {
      setSubmitting(false);
      setSubmitStatus("error");

      if (err.response?.status === 422) {
        const validationErrors = err.response.data.errors || {};
        const mappedErrors = {};
        
        Object.entries(validationErrors).forEach(([key, messages]) => {
          mappedErrors[key] = Array.isArray(messages) ? messages : [messages];
        });
        
        setErrors(mappedErrors);
        toast.error(err.response.data.message || "Please fix the errors below.");
      } else {
        setErrors({
          general: [err.response?.data?.message || "Registration failed. Please try again."]
        });
        toast.error(err.response?.data?.message || "Registration failed. Please try again.");
      }
    }
  };

  const renderDynamicField = (field) => {
    const value = formData.answers?.[field.key] ?? (field.type === "checkbox" ? false : "");
    const fieldError = getFieldError(`answers.${field.key}`);
    const isFocused = focusedField === field.key;

    switch (field.type) {
      case "textarea":
        return (
          <div className="webinar-input-group" key={field.key}>
            <label className={`webinar-label ${isFocused ? "focused" : ""}`} htmlFor={field.key}>
              {field.label}
              {field.required && <span className="webinar-required">*</span>}
            </label>
            <div className={`webinar-field-wrapper ${isFocused ? "focused" : ""} ${fieldError ? "error" : ""}`}>
              <textarea
                id={field.key}
                className="webinar-input webinar-textarea"
                value={value}
                onChange={(e) => handleAnswerChange(field.key, e.target.value)}
                onFocus={() => setFocusedField(field.key)}
                onBlur={() => setFocusedField(null)}
                required={field.required}
                rows={3}
                placeholder={`Tell us about ${field.label.toLowerCase()}...`}
              />
            </div>
            {fieldError && <span className="webinar-field-error">{fieldError}</span>}
          </div>
        );

      case "select":
        return (
          <div className="webinar-input-group" key={field.key}>
            <label className={`webinar-label ${isFocused ? "focused" : ""}`} htmlFor={field.key}>
              {field.label}
              {field.required && <span className="webinar-required">*</span>}
            </label>
            <div className={`webinar-field-wrapper select-wrapper ${isFocused ? "focused" : ""} ${fieldError ? "error" : ""}`}>
              <select
                id={field.key}
                className="webinar-input webinar-select"
                value={value}
                onChange={(e) => handleAnswerChange(field.key, e.target.value)}
                onFocus={() => setFocusedField(field.key)}
                onBlur={() => setFocusedField(null)}
                required={field.required}
              >
                <option value="">Select an option</option>
                {(field.options || []).map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            {fieldError && <span className="webinar-field-error">{fieldError}</span>}
          </div>
        );

      case "checkbox":
        return (
          <div className="webinar-input-group webinar-checkbox-group" key={field.key}>
            <label className="webinar-checkbox-label" htmlFor={field.key}>
              <input
                id={field.key}
                type="checkbox"
                checked={!!value}
                onChange={(e) => handleAnswerChange(field.key, e.target.checked)}
                required={field.required}
              />
              <span className="checkbox-box" />
              <span className="checkbox-text">
                {field.label}
                {field.required && <span className="webinar-required">*</span>}
              </span>
            </label>
            {fieldError && <span className="webinar-field-error">{fieldError}</span>}
          </div>
        );

      default:
        return (
          <div className="webinar-input-group" key={field.key}>
            <label className={`webinar-label ${isFocused ? "focused" : ""}`} htmlFor={field.key}>
              {field.label}
              {field.required && <span className="webinar-required">*</span>}
            </label>
            <div className={`webinar-field-wrapper ${isFocused ? "focused" : ""} ${fieldError ? "error" : ""}`}>
              <input
                id={field.key}
                className="webinar-input"
                type={field.type === "phone" ? "tel" : field.type}
                value={value}
                onChange={(e) => handleAnswerChange(field.key, e.target.value)}
                onFocus={() => setFocusedField(field.key)}
                onBlur={() => setFocusedField(null)}
                required={field.required}
                placeholder={`Enter ${field.label.toLowerCase()}`}
              />
            </div>
            {fieldError && <span className="webinar-field-error">{fieldError}</span>}
          </div>
        );
    }
  };

  if (loadingWebinar) {
    return (
      <div className="webinar-loading">
        <div className="webinar-spinner-container">
          <div className="webinar-spinner" />
          <div className="webinar-spinner-inner" />
        </div>
        <p>Fetching webinar details...</p>
      </div>
    );
  }

  if (notFound || !webinar) {
    return (
      <div className="webinar-not-found-container">
        <div className="webinar-not-found-card">
          <div className="not-found-icon">🔍</div>
          <h2>Webinar Not Found</h2>
          <p>The webinar you're looking for doesn't exist or has been removed.</p>
          <Link to="/webinars" className="webinar-btn-outline back-home-btn">
            View Webinars
          </Link>
        </div>
      </div>
    );
  }

  if (submitStatus === "success") {
    return (
      <div className="webinar-success-container">
        <div className="webinar-success-blur-bg" />
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="webinar-success-card"
        >
          <div className="success-icon-wrapper">
            <FaCheckCircle className="success-checkmark-icon" />
          </div>
          <h2>Registration Confirmed!</h2>
          <p className="webinar-success-msg">
            Your spot for <strong>{webinar.title}</strong> has been secured. 
            A confirmation email with the details has been sent to <strong>{registrationData?.email}</strong>.
          </p>

          <div className="webinar-success-details">
            <div className="success-row">
              <FaCalendarAlt className="success-row-icon" />
              <div className="success-row-text">
                <span className="row-label">Date</span>
                <span className="row-val">{formatDate(webinar.starts_at)}</span>
              </div>
            </div>
            <div className="success-row">
              <FaClock className="success-row-icon" />
              <div className="success-row-text">
                <span className="row-label">Time & Duration</span>
                <span className="row-val">
                  {formatTime(webinar.starts_at)} ({webinar.duration_minutes} minutes)
                </span>
              </div>
            </div>
          </div>

          <div className="success-actions">
            <a 
              href={getGoogleCalendarLink()} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="calendar-btn"
            >
              <FaCalendarPlus />
              <span>Add to Google Calendar</span>
            </a>
            <Link to="/webinars" className="back-webinars-btn">
              Back to Webinars
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  const isBaseFocused = (name) => focusedField === name;

  return (
    <div className="webinar-register-page">
      <div className="webinar-register-wrapper">
        
        {/* Left Information Panel */}
        <div className="webinar-info-panel">
          <div className="panel-overlay-pattern" />
          <div className="info-panel-inner">
            <Link to="/webinars" className="register-back-link">
              <FaArrowLeft />
              <span>Back to Webinars</span>
            </Link>

            <motion.div 
              initial={{ opacity: 0, x: -25 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="webinar-details-card"
            >
              <span className="register-badge-tag">GritinAI Webinar</span>
              <h1 className="register-webinar-title">{webinar.title}</h1>
              
              {webinar.description && (
                <p className="register-webinar-desc">{webinar.description}</p>
              )}

              <div className="register-meta-list">
                <div className="register-meta-item">
                  <div className="meta-icon-box">
                    <FaCalendarAlt />
                  </div>
                  <div className="meta-item-content">
                    <span className="meta-label">DATE</span>
                    <span className="meta-val">{formatDate(webinar.starts_at)}</span>
                  </div>
                </div>

                <div className="register-meta-item">
                  <div className="meta-icon-box">
                    <FaClock />
                  </div>
                  <div className="meta-item-content">
                    <span className="meta-label">TIME</span>
                    <span className="meta-val">{formatTime(webinar.starts_at)}</span>
                  </div>
                </div>

                <div className="register-meta-item">
                  <div className="meta-icon-box">
                    <FaClock style={{ transform: "rotate(90deg)" }} />
                  </div>
                  <div className="meta-item-content">
                    <span className="meta-label">DURATION</span>
                    <span className="meta-val">{webinar.duration_minutes} Minutes</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Right Form Panel */}
        <div className="webinar-form-panel">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="register-form-container"
          >
            <div className="form-title-block">
              <h2>Join the Webinar</h2>
              <p>Fill out the form below to secure your complimentary ticket.</p>
            </div>

            <form ref={formRef} onSubmit={handleSubmit} className="register-main-form" noValidate>
              {/* Global Error Alert Banner */}
              {getFieldError('general') && (
                <div className="form-global-error" role="alert">
                  <span>{getFieldError('general')}</span>
                </div>
              )}

              {/* Full Name Field */}
              <div className="webinar-input-group">
                <label className={`webinar-label ${isBaseFocused('name') ? "focused" : ""}`} htmlFor="w-name">
                  Full Name <span className="webinar-required">*</span>
                </label>
                <div className={`webinar-field-wrapper ${isBaseFocused('name') ? "focused" : ""} ${getFieldError('name') ? "error" : ""}`}>
                  <FaUser className="field-icon" />
                  <input
                    id="w-name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleBaseChange}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="E.g. Jane Doe"
                    required
                  />
                </div>
                {getFieldError('name') && (
                  <span className="webinar-field-error">{getFieldError('name')}</span>
                )}
              </div>

              {/* Email Address Field */}
              <div className="webinar-input-group">
                <label className={`webinar-label ${isBaseFocused('email') ? "focused" : ""}`} htmlFor="w-email">
                  Email Address <span className="webinar-required">*</span>
                </label>
                <div className={`webinar-field-wrapper ${isBaseFocused('email') ? "focused" : ""} ${getFieldError('email') ? "error" : ""}`}>
                  <FaEnvelope className="field-icon" />
                  <input
                    id="w-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleBaseChange}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="jane.doe@example.com"
                    required
                  />
                </div>
                {getFieldError('email') && (
                  <span className="webinar-field-error">{getFieldError('email')}</span>
                )}
              </div>

              {/* Phone Number Field */}
              <div className="webinar-input-group">
                <label className={`webinar-label ${isBaseFocused('phone') ? "focused" : ""}`} htmlFor="w-phone">
                  Phone Number <span className="webinar-optional">(optional)</span>
                </label>
                <div className={`webinar-field-wrapper ${isBaseFocused('phone') ? "focused" : ""} ${getFieldError('phone') ? "error" : ""}`}>
                  <FaPhone className="field-icon" />
                  <input
                    id="w-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleBaseChange}
                    onFocus={() => setFocusedField('phone')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="+234 800 000 0000"
                  />
                </div>
                {getFieldError('phone') && (
                  <span className="webinar-field-error">{getFieldError('phone')}</span>
                )}
              </div>

              {/* Render Custom Dynamic Fields */}
              {(webinar.form_fields || []).map(renderDynamicField)}

              {/* Submit Button */}
              <button
                type="submit"
                className={`register-submit-button ${submitting ? "loading" : ""}`}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="btn-spinner" />
                    <span>Reserving Spot...</span>
                  </>
                ) : (
                  <span>Register Now</span>
                )}
              </button>
            </form>
          </motion.div>
        </div>

      </div>
    </div>
  );
}
